/**
 * Sword sweep-attack particle: vanilla's `sweep_attack` is an 8-frame white
 * crescent shown ~1 block in front of the attacker at chest height. Drawn here
 * as one camera-facing quad playing the real sweep_0..7 sprites over 8 ticks.
 */

import * as THREE from 'three';

const LIFE = 0.4;

/** The eight real vanilla sweep_0..7 particle frames, played over the effect's life. */
function sweepFrames(): THREE.Texture[] {
  const loader = new THREE.TextureLoader();
  return Array.from({ length: 8 }, (_, i) => {
    const t = loader.load(`mc/particle/sweep_${i}.png`);
    t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
    return t;
  });
}

export class SweepFx {
  private readonly mesh: THREE.Mesh;
  private readonly mat: THREE.MeshBasicMaterial;
  private life = 0;

  private readonly frames = sweepFrames();

  constructor(scene: THREE.Scene) {
    this.mat = new THREE.MeshBasicMaterial({ map: this.frames[0], transparent: true, depthWrite: false, side: THREE.DoubleSide });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.mat);
    this.mesh.visible = false;
    this.mesh.renderOrder = 10;
    scene.add(this.mesh);
  }

  /** Show a sweep in front of the camera. */
  spawn(camera: THREE.Camera): void {
    const dir = new THREE.Vector3();
    camera.getWorldDirection(dir);
    dir.y = 0;
    if (dir.lengthSq() < 1e-6) dir.set(0, 0, -1);
    dir.normalize();
    const p = new THREE.Vector3();
    camera.getWorldPosition(p);
    this.mesh.position.set(p.x + dir.x * 1.2, p.y - 0.4, p.z + dir.z * 1.2);
    this.mesh.quaternion.setFromEuler(new THREE.Euler(0, Math.atan2(-dir.x, -dir.z), 0));
    this.life = LIFE;
    this.mesh.visible = true;
  }

  update(dt: number): void {
    if (this.life <= 0) return;
    this.life -= dt;
    const f = Math.min(7, Math.floor((1 - Math.max(0, this.life) / LIFE) * 8));
    this.mat.map = this.frames[f];
    this.mesh.scale.setScalar(1 + (1 - this.life / LIFE) * 0.3);
    if (this.life <= 0) this.mesh.visible = false;
  }
}
